import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as Sentry from '@sentry/angular';
import { ANALYTICS_FALLBACK_USER_NAME } from '@shared';
import { catchError, from, map, of, switchMap, tap, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import * as fromAuth from '../../auth/store/auth.reducer';
import * as fromLayout from '../../layout';
import { ldClient } from '../launch-darkly.client';
import { FeatureFlagsActions } from './feature-flags.actions';
import { FeatureFlagsState } from './feature-flags.reducer';

/** LaunchDarkly's v3 `identify()` has no timeout of its own. */
const IDENTIFY_TIMEOUT_MS = 5000;

@Injectable()
export class FeatureFlagsEffects {
  private readonly actions$ = inject(Actions);
  private readonly authStore = inject(Store<fromAuth.AuthState>);
  private readonly layoutStore = inject(Store<fromLayout.LayoutState>);
  private readonly featureFlagsStore = inject(Store<FeatureFlagsState>);

  identifyUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeatureFlagsActions.identifyUser),
      switchMap(({ user }) => {
        const isMobile = this.layoutStore.selectSignal(
          fromLayout.layoutFeature.selectIsMobile,
        )();
        const deviceType = isMobile ? 'mobile' : 'desktop';
        // No per-user evaluation (no client, identify failed or hung): `ready` still lands, over the
        // boot values, so a reader waiting on it acts as it did before `ready` existed.
        const bootValues = FeatureFlagsActions.userIdentified({
          featureFlags: [],
        });

        return ldClient
          ? from(
              /**
               * Keys must be consistent, which means the same person must correspond
               * to the same key across different services to contribute to consistent
               * flag evaluations.
               * https://docs.launchdarkly.com/sdk/features/context-config
               */
              ldClient.identify({
                ...environment.launchDarkly.context,
                user: {
                  kind: 'user',
                  key: user.email,
                  email: user.email,
                  name: user.fullName ?? ANALYTICS_FALLBACK_USER_NAME,
                  sub: user.id,
                  anonymous: false,
                },
                device: {
                  key: deviceType,
                  name: deviceType,
                },
              }),
            ).pipe(
              timeout(IDENTIFY_TIMEOUT_MS),
              // The resolved set lands with `ready`, so a reader never sees `ready` beside a stale
              // anonymous value.
              map((flags) =>
                FeatureFlagsActions.userIdentified({
                  featureFlags: Object.entries(flags).map(([key, value]) => ({
                    key,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    value,
                  })),
                }),
              ),
              catchError((error: unknown) => {
                Sentry.captureException(error);
                return of(bootValues);
              }),
            )
          : of(bootValues);
      }),
    ),
  );

  onSetIsMobile$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromLayout.LayoutActions.setIsMobile),
        tap(() => {
          const user = this.authStore.selectSignal(
            fromAuth.authFeature.selectUser,
          )();
          if (user) {
            this.featureFlagsStore.dispatch(
              FeatureFlagsActions.identifyUser({ user }),
            );
          }
        }),
      ),
    { dispatch: false },
  );
}
